/**
 * Store de autenticación — Estado global de usuario y sesión (Zustand)
 * Exporta: useAuthStore
 */
import { logger } from "@/lib/logger";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { User, AuthResponse, Business } from "@/types";
import { clearAllCajaIngressAcknowledgments } from "@/features/caja/utils/cajaIngress";

interface AuthState {
  user: User | null;
  business: Business | null;
  token: string | null;
  isAuthenticated: boolean;
  login: (data: AuthResponse) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  updateBusiness: (business: Business | null) => void;
}

/**
 * Store de autenticación con Zustand
 * Persiste el estado en localStorage
 */
export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      business: null,
      token: null,
      isAuthenticated: false,

      login: (data: AuthResponse) => {
        set({
          user: data.user,
          business: data.business,
          token: data.token,
          isAuthenticated: true,
        });
      },

      logout: () => {
        logger.log("🚪 Ejecutando logout - Limpiando sesión...");
        clearAllCajaIngressAcknowledgments();
        set({
          user: null,
          business: null,
          token: null,
          isAuthenticated: false,
        });
        // Force clear localStorage to ensure complete cleanup
        localStorage.removeItem("altenot-auth");
        logger.log("✅ Sesión limpiada completamente");
      },

      updateUser: (userData: Partial<User>) => {
        set((state) => ({
          user: state.user ? { ...state.user, ...userData } : null,
        }));
      },

      updateBusiness: (business: Business | null) => {
        set({ business });
      },
    }),
    {
      name: "altenot-auth",
    },
  ),
);
